import type { ReactNode } from "react";
import { Text } from "@ferrivia/ui";
import s from "./ResultSlip.module.css";

/* One draw, as one object: a header strip saying when the sample was taken
   and where the numbers came from, then that draw's readings as rows inside
   it.

   The shape is the home screen's latest-result card — direction D2 in
   product/prototypes/home-result-card.md, built as the client app's
   LatestResultBlock — applied to a whole draw instead of a single
   measurement. Carrying provenance as structure rather than as a sentence is
   what let the Results tab drop the free-floating "Näyte otettu …" line it
   used to set above the readings: the line did not shrink, it moved into the
   head, where it now labels the readings it belongs to instead of floating
   above them.

   Nothing here knows what any of the readings say. It is a frame, and the
   hairlines between its children are the only thing it adds to them. */
export interface ResultSlipProps {
  /** When the sample was taken, already formatted by the caller. */
  when: string;
  /** How this surface says where the numbers came from — the client app's
      self-reported mark, the clinic console's laboratory chip and, for a
      self-reported draw, where the customer read them off. Sits at the
      head's right and wraps under the date on a narrow pane. Omit on a
      surface that says nothing about provenance. */
  provenance?: ReactNode;
  children: ReactNode;
}

export function ResultSlip({ when, provenance, children }: ResultSlipProps) {
  return (
    <div className={s.root}>
      <div className={s.head}>
        <Text as="span" size="xs" weight="medium" className={s.when}>
          {when}
        </Text>
        {provenance !== undefined && <span className={s.provenance}>{provenance}</span>}
      </div>
      <div className={s.rows}>{children}</div>
    </div>
  );
}
